import { useEffect, useState } from "react";
import { HelpCircle, MessageSquare, X, Maximize2, Minimize2 } from "lucide-react";
import { useHelpWidget } from "./help-widget-context";
import { HelpSection } from "./help-articles";
import { MessageSection } from "./help-message";
import { cn } from "@/lib/utils";

/**
 * Frame.io-style Help & Support widget. Opened from the rail Help icon; floats
 * bottom-right as a compact panel and can expand to a large centered overlay.
 * Two sections via the bottom tab bar: Help (search + articles, step 2) and
 * Message us (contact form, step 3).
 *
 * Rendered once at the layout level (see app-layout / admin-layout) so it
 * overlays the whole app. Desktop-only chrome; on mobile it still opens but
 * fills most of the viewport via the max-w/max-h clamps.
 */
export function HelpWidget() {
  const { open, tab, setTab, close } = useHelpWidget();
  const [expanded, setExpanded] = useState(false);

  // Collapse back to the compact panel whenever the widget is closed, so it
  // always reopens small.
  useEffect(() => {
    if (!open) setExpanded(false);
  }, [open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  if (!open) return null;

  const card = (
    <div
      role="dialog"
      aria-label="Help and support"
      onClick={(e) => e.stopPropagation()}
      className={cn(
        "pointer-events-auto flex flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-2xl",
        expanded
          ? "h-full w-[640px] max-w-[calc(100vw-2rem)]"
          : "h-[620px] max-h-[calc(100vh-2rem)] w-[400px] max-w-[calc(100vw-2rem)]"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <span className="text-sm font-semibold">
          {tab === "help" ? "Help center" : "Message us"}
        </span>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            aria-label={expanded ? "Shrink" : "Expand"}
            onClick={() => setExpanded((v) => !v)}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button
            type="button"
            aria-label="Close help"
            onClick={close}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content — Help (step 2) + Message us (step 3) */}
      <div className="flex min-h-0 flex-1 flex-col">
        {tab === "help" ? (
          <HelpSection onGoToMessage={() => setTab("message")} />
        ) : (
          <MessageSection onGoToHelp={() => setTab("help")} />
        )}
      </div>

      {/* Bottom tab bar */}
      <div className="grid grid-cols-2 border-t border-border">
        <TabButton active={tab === "help"} icon={HelpCircle} label="Help" onClick={() => setTab("help")} />
        <TabButton active={tab === "message"} icon={MessageSquare} label="Message us" onClick={() => setTab("message")} />
      </div>
    </div>
  );

  // Anchored to the right edge in BOTH sizes — expand just grows the panel
  // (taller + wider), it does not jump to the center. App stays usable behind.
  return (
    <div
      className={cn(
        "pointer-events-none fixed right-4 z-[60]",
        expanded ? "bottom-4 top-4" : "bottom-4"
      )}
    >
      {card}
    </div>
  );
}

function TabButton({
  active,
  icon: Icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: typeof HelpCircle;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-col items-center gap-1 py-2.5 text-xs font-medium transition-colors",
        active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={1.75} />
      {label}
    </button>
  );
}
